import type { MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    EventCard,
    EventsGrid,
    CollapsibleSection,
    Reasoning,
} from '@/components';
import { QueryIdentities } from './QueryIdentities';
import { ReasoningSubDescription } from './ReasoningSubDescription';
import { useRotatingDescription } from '../hooks/useRotatingDescription';
import {
    getBlurbIntervalMs,
    getReasoningPhase,
} from '../utils/reasoning-phase';
import { buildReasoningBlurbs } from '../utils/reasoning-blurbs';
import { EngineService } from '@/services/engine';
import { DateTimeMode, getDateTimeLabel } from '@/utils/query-filters';
import {
    buildAdditionalResultsSteps,
    buildPrimarySteps,
} from '@/utils/query-reasoning-steps';
import {
    useQueryFiltersStore,
    useQueryResultsStore,
    useSourcesStore,
} from '@/stores';
import {
    useAddEventToReportMenuItem,
    useExportEventMenuItem,
    useVideoSourceSelected,
} from '@/hooks';
import type { EventsExportControls } from '@/hooks';
import { usePipEventWatcher } from '@/stores/pip-player.store';
import type { ScoredEvent } from '@/types/event';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { buildEventCardMetadataFromEvent } from '@/utils/event-card-metadata';

interface QueryResultSectionProps {
    events: ScoredEvent[];
    isLoading: boolean;
    additionalResults?: boolean;
    query: string | null;
    hasAttachment: boolean;
    onEventClick: (eventId: string, event: MouseEvent) => void;
    onQuickView: (eventId: string) => void;
    eventExport: EventsExportControls;
}

export function QueryResultSection({
    events,
    isLoading,
    additionalResults = false,
    query,
    hasAttachment,
    onEventClick,
    onQuickView,
    eventExport,
}: QueryResultSectionProps) {
    const { t } = useTranslation([
        I18nNamespace.COMMON,
        I18nNamespace.EVENTS,
        I18nNamespace.SOURCES,
        I18nNamespace.QUERIES,
    ]);
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const isPipPlaying = usePipEventWatcher();
    const addToReportItem = useAddEventToReportMenuItem();
    const exportEventItem = useExportEventMenuItem();

    const selectionMode = useQueryResultsStore(
        (state) => state.mode === 'selection',
    );
    const selectedEventIds = useQueryResultsStore(
        (state) => state.selectedEventIds,
    );
    const toggleEventSelection = useQueryResultsStore(
        (state) => state.toggleEventSelection,
    );
    const vehicleResults = useQueryResultsStore(
        (state) => state.vehicleResults,
    );
    const personResults = useQueryResultsStore((state) => state.personResults);
    const analyzedQueries = useQueryResultsStore(
        (state) => state.analyzedQueries,
    );
    const querySummary = useQueryResultsStore((state) => state.querySummary);
    const loadMoreSummary = useQueryResultsStore(
        (state) => state.loadMoreSummary,
    );

    const identityCount = additionalResults
        ? 0
        : (vehicleResults?.length ?? 0) + (personResults?.length ?? 0);

    const [frozenCounts, setFrozenCounts] = useState<{
        eventCount: number;
        sourceCount: number;
    } | null>(null);

    useEffect(() => {
        if (isLoading) {
            setFrozenCounts(null);
            return;
        }
        if (events.length > 0) {
            setFrozenCounts({
                eventCount: events.length,
                sourceCount: new Set(events.map((e) => e.event.sourceId)).size,
            });
        }
    }, [isLoading, events]);

    const eventCount = frozenCounts?.eventCount ?? events.length;
    const sourceCount =
        frozenCounts?.sourceCount ??
        new Set(events.map((e) => e.event.sourceId)).size;

    const summary = useMemo(() => {
        if (additionalResults) {
            return loadMoreSummary ?? undefined;
        }
        return querySummary && querySummary.length > 0
            ? querySummary
            : undefined;
    }, [additionalResults, querySummary, loadMoreSummary]);

    const steps = useMemo(
        () =>
            additionalResults
                ? buildAdditionalResultsSteps(
                      t,
                      eventCount,
                      sourceCount,
                      isLoading,
                      query,
                  )
                : buildPrimarySteps(
                      t,
                      eventCount,
                      sourceCount,
                      identityCount,
                      isLoading,
                      query,
                      hasAttachment,
                      analyzedQueries,
                  ),
        [
            t,
            additionalResults,
            hasAttachment,
            analyzedQueries,
            isLoading,
            eventCount,
            sourceCount,
            identityCount,
            query,
        ],
    );

    const allSources = useSourcesStore((state) => state.sources);
    const selectedSources = useSourcesStore((state) => state.selected);
    const excludedSources = useSourcesStore((state) => state.excluded);
    const allSelected = useSourcesStore((state) => state.all);
    const videoSourceSelected = useVideoSourceSelected(selectedSources);
    const dateTime = useQueryFiltersStore((state) => state.filters.dateTime);
    const confidence = useQueryFiltersStore(
        (state) => state.filters.confidence,
    );

    const phase = additionalResults
        ? null
        : getReasoningPhase({
              isLoading,
              analyzedQueries,
              eventCount,
              identityCount,
          });

    const blurbs = useMemo(() => {
        const searchedSourceCount = allSelected
            ? Math.max(0, allSources.length - excludedSources.size)
            : selectedSources.size;
        const rawRange = getDateTimeLabel(dateTime).toLowerCase();
        const timeRangeLabel =
            dateTime.mode === DateTimeMode.ALL || videoSourceSelected
                ? null
                : rawRange.startsWith('last')
                  ? `the ${rawRange}`
                  : rawRange;
        return buildReasoningBlurbs(t, phase, {
            query,
            hasAttachment,
            sourceCount: searchedSourceCount,
            timeRangeLabel,
            confidenceMin: confidence.enabled ? confidence.min : null,
            analyzed: analyzedQueries?.[0] ?? null,
        });
    }, [
        t,
        phase,
        query,
        hasAttachment,
        allSelected,
        allSources.length,
        excludedSources.size,
        selectedSources.size,
        videoSourceSelected,
        dateTime,
        confidence.enabled,
        confidence.min,
        analyzedQueries,
    ]);

    const subDescription = useRotatingDescription(
        blurbs,
        getBlurbIntervalMs(phase),
    );

    const noResults =
        !additionalResults &&
        !isLoading &&
        analyzedQueries !== null &&
        eventCount === 0 &&
        identityCount === 0;

    const renderEventCard = (scored: ScoredEvent) => {
        const event = scored.event;
        const isWatchingThis = isPipPlaying(event.id);
        const metadata = buildEventCardMetadataFromEvent(
            event,
            getSourceById(event.sourceId),
        );
        const title =
            query?.trim() ||
            t('page.eventDetected', {
                ns: I18nNamespace.EVENTS,
            });
        return (
            <EventCard
                key={event.id}
                id={event.id}
                title={title}
                thumbnailUrls={[
                    event.thumbnailUrl ||
                        EngineService.getEventThumbnailUrl(event.id, true),
                ]}
                confidence={scored.confidence}
                isScene={event.type === 'SCENE'}
                metadata={metadata}
                quickView={{
                    state: isWatchingThis ? 'watching' : 'idle',
                    onClick: () => onQuickView(event.id),
                }}
                onClick={onEventClick}
                extraActions={[
                    ...addToReportItem(event, {
                        query,
                        confidence: scored.confidence,
                    }),
                    ...exportEventItem(event, eventExport),
                ]}
                selectable={selectionMode}
                selected={selectedEventIds.has(event.id)}
                onSelectToggle={toggleEventSelection}
            />
        );
    };

    const showSection =
        additionalResults ||
        isLoading ||
        analyzedQueries !== null ||
        events.length > 0;

    if (!showSection) {
        return null;
    }

    return (
        <div>
            <Reasoning
                steps={steps}
                summary={summary}
                isThinking={isLoading}
                animatePhraseChange={!additionalResults}
                defaultOpen={noResults}
                icon={
                    <MagnifyingGlassIcon
                        size={16}
                        className={cn(
                            'transition-colors',
                            isLoading
                                ? 'text-grey-100 animate-pulse'
                                : 'text-grey-300',
                        )}
                    />
                }
            />

            <ReasoningSubDescription text={subDescription} />

            {!additionalResults && (
                <div className="pl-3">
                    <QueryIdentities />
                </div>
            )}

            {events.length > 0 && (
                <div className="pl-3">
                    <CollapsibleSection
                        label={t('sections.events', {
                            ns: I18nNamespace.QUERIES,
                        })}
                        count={events.length}
                    >
                        <EventsGrid>{events.map(renderEventCard)}</EventsGrid>
                    </CollapsibleSection>
                </div>
            )}
        </div>
    );
}
